
import { Component, Prop, Vue } from "vue-property-decorator";
import {isInMobileApp} from "src/utils/MobileUtils";

@Component
export default class TeacherSpeechRecognition extends Vue {
  @Prop({ type: Boolean, default: false }) readonly initialListening!: boolean;

  private isListening: boolean = false;
  private isSpeaking: boolean = false;
  private soundDetectionTimeout: any = null;
  private recognition: any = null;
  private speechRecognitionNotAvailable = false;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private audioDataArray: Uint8Array | null = null;
  private audioStream: MediaStream | null = null;
  private soundThreshold = 15;
  private audioProcessInterval: number | null = null;

  mounted() {
    this.setupSpeechRecognition();
    if (this.initialListening) {
      this.startListening();
    }
  }

  beforeDestroy() {
    this.stopListening();
    this.cleanupAudioContext();
  }

  setupSpeechRecognition() {
    if (!('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)) {
      this.speechRecognitionNotAvailable = true;
      this.$emit('update:speechRecognitionNotAvailable', true);
      return;
    }

    const SpeechRecognition = window.webkitSpeechRecognition || window.SpeechRecognition;
    this.recognition = new SpeechRecognition();
    this.recognition.lang = 'fr-FR';
    this.recognition.continuous = true;
    this.recognition.interimResults = false;

    this.recognition.onresult = (event) => {
      const text = event.results[event.results.length - 1][0].transcript.trim();
      if (text) {
        this.$emit('text-recognized', text);
      }
    };

    this.recognition.onerror = (event) => {
      if (event.error !== 'no-speech') {
        this.stopListening();
      }
    };

    this.recognition.onend = () => {
      if (this.isListening) {
        this.recognition.start();
      }
    };
  }

  async setupAudioDetector() {
    try {
      this.cleanupAudioContext();

      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 64;
      this.audioDataArray = new Uint8Array(this.analyser.frequencyBinCount);

      this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.audioContext.createMediaStreamSource(this.audioStream).connect(this.analyser);

      this.audioProcessInterval = window.setInterval(() => {
        if (!this.isListening || !this.analyser || !this.audioDataArray) {
          return;
        }

        this.analyser.getByteFrequencyData(this.audioDataArray);

        let sum = 0;
        for (let i = 0; i < this.audioDataArray.length; i++) {
          sum += this.audioDataArray[i];
        }

        if (sum / this.audioDataArray.length > this.soundThreshold) {
          if (!this.isSpeaking) {
            this.isSpeaking = true;
            this.$emit('update:isSpeaking', true);
          }

          clearTimeout(this.soundDetectionTimeout);
          this.soundDetectionTimeout = setTimeout(() => {
            this.isSpeaking = false;
            this.$emit('update:isSpeaking', false);
          }, 300);
        }
      }, 100);
    }
    catch (error) {}
  }

  cleanupAudioContext() {
    if (this.audioProcessInterval) {
      clearInterval(this.audioProcessInterval);
      this.audioProcessInterval = null;
    }

    if (this.audioStream) {
      this.audioStream.getTracks().forEach(track => track.stop());
      this.audioStream = null;
    }

    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch(() => {});
    }

    this.audioContext = null;
    this.analyser = null;
    this.audioDataArray = null;
  }

  toggleVoiceRecognition() {
    if (this.speechRecognitionNotAvailable) {
      this.$emit('new-suggestion', {
        message: "Le mode écoute n'est pas disponible dans ce navigateur. Veuillez utiliser un autre navigateur (Google Chrome, Safari, Edge, etc.)",
        timeout: 2000
      });
      return;
    }

    this.isListening ? this.stopListening() : this.startListening();
  }

  startListening() {
    if (!this.recognition || this.isListening) {
      return;
    }

    try {
      this.recognition.start();
      this.isListening = true;
      this.$emit('update:isListening', true);
      if (!isInMobileApp()) {
        this.setupAudioDetector();
      }

      this.$emit('new-suggestion', {
        message: "Mode écoute activé, parlez pour m'envoyer un message.",
        timeout: 2000
      });
    }
    catch {}
  }

  stopListening() {
    if (!this.recognition || !this.isListening) {
      return;
    }

    try {
      this.recognition.stop();
      this.isListening = false;
      this.$emit('update:isListening', false);
      this.cleanupAudioContext();

      this.$emit('new-suggestion', {
        message: "J'ai désactivé le mode écoute.",
        timeout: 2000
      });
    }
    catch {}
  }

  public get isSpeechRecognitionNotAvailable(): boolean {
    return this.speechRecognitionNotAvailable;
  }
}
